import { type QueryKey, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import React from 'react';

import { fsApi } from './api';
import {
  CoreService,
  type GetActiveUserResponse,
  type GetWebConfigResponse,
  type ListFeatureSetReviewsPageResponse,
  type ListFeatureSetsToReviewResponse,
  type RecentlyUsedFeatureSetsResponse,
  type RecentlyUsedProjectsResponse,
  ReviewStatus,
} from './gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { getErrorMessageBarMessage } from './utils';

export const featurestoreQueryKeys = {
  activeUser: [`featurestore`, `activeUser`] as const,
  featureSetsReview: (filters?: string[]) => {
    const baseKey: QueryKey = [`featurestore`, `featureSetsReview`];
    if (!filters) return baseKey;
    return [...baseKey, { filters }] as const;
  },
  featureSetsToReview: (filters?: string[]) => {
    const baseKey: QueryKey = [`featurestore`, `featureSetsToReview`];
    if (!filters) return baseKey;
    return [...baseKey, { filters }] as const;
  },
  recentlyOpenedFeatureSets: [`featurestore`, `recentlyOpenedFeatureSets`] as const,
  recentlyUsedProjects: [`featurestore`, `recentlyUsedProjects`] as const,
  webConfig: [`featurestore`, `webConfig`] as const,
};

export type ActiveUserOptions<TData> = UseQueryOptions<GetActiveUserResponse, unknown, TData>;
export const useQueryActiveUser = <TData = GetActiveUserResponse>(
  options?: Omit<ActiveUserOptions<TData>, 'queryFn' | 'queryKey'>
) => {
  const baseOptions: ActiveUserOptions<TData> = {
    queryFn: () => fsApi(CoreService.GetActiveUser),
    queryKey: featurestoreQueryKeys.activeUser,
  };
  return useQuery({
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
    ...baseOptions,
  });
};

export type WebConfigOptions<TData> = UseQueryOptions<GetWebConfigResponse, unknown, TData>;
export const useQueryWebConfig = <TData = GetWebConfigResponse>(
  options?: Omit<WebConfigOptions<TData>, `queryFn` | `queryKey`>
) => {
  const baseOptions: WebConfigOptions<TData> = {
    queryFn: () => fsApi(CoreService.GetWebConfig),
    queryKey: featurestoreQueryKeys.webConfig,
  };
  return useQuery({
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
    ...baseOptions,
  });
};

export const useHasReviewerRole = (options?: Pick<UseQueryOptions, `enabled`>) => {
  const activeUserQuery = useQueryActiveUser(options);
  const webConfigQuery = useQueryWebConfig(options);
  return React.useMemo(() => {
    if (!activeUserQuery.data?.userRoles?.length || !webConfigQuery.data?.reviewRoles?.length) return false;
    const reviewerRoles = new Set(webConfigQuery.data.reviewRoles);
    const userRoles = new Set(activeUserQuery.data.userRoles);
    // @ts-expect-error new Set methods not available in TypeScript < 5.5
    const isNotReviewer = userRoles.isDisjointFrom(reviewerRoles);
    return !isNotReviewer;
  }, [activeUserQuery.data, webConfigQuery.data]);
};

export type PendingReviewCountOptions<TData> = UseQueryOptions<ListFeatureSetsToReviewResponse, unknown, TData>;
export const useQueryPendingReviews = <TData = ListFeatureSetsToReviewResponse>(
  options?: Omit<PendingReviewCountOptions<TData>, `queryFn` | `queryKey`>
) => {
  const filters = [ReviewStatus.REVIEW_STATUS_TO_REVIEW];
  const baseOptions: PendingReviewCountOptions<TData> = {
    queryFn: () => fsApi(CoreService.ListFeatureSetsToReview, { filters, pageSize: 1000 }),
    queryKey: featurestoreQueryKeys.featureSetsToReview(filters),
  };
  return useQuery({
    meta: {
      toastError: (err: unknown) =>
        getErrorMessageBarMessage(err, `An error has occurred while loading pending review count: `),
    },
    refetchInterval: 60 * 1000, // 1 minute
    ...options,
    ...baseOptions,
  });
};

export type PendingFeatureSetCountOptions<TData> = UseQueryOptions<ListFeatureSetReviewsPageResponse, unknown, TData>;
export const useQueryPendingFeatureSets = <TData = ListFeatureSetReviewsPageResponse>(
  options?: Omit<PendingFeatureSetCountOptions<TData>, `queryFn` | `queryKey`>
) => {
  const filters = [ReviewStatus.REVIEW_STATUS_CREATED, ReviewStatus.REVIEW_STATUS_TO_REVIEW];
  const baseOptions: PendingFeatureSetCountOptions<TData> = {
    queryFn: () => fsApi(CoreService.ListFeatureSetsReviewsPage, { filters, pageSize: 1000 }),
    queryKey: featurestoreQueryKeys.featureSetsReview(filters),
  };
  return useQuery({
    meta: {
      toastError: (err: unknown) =>
        getErrorMessageBarMessage(err, `An error has occurred while loading pending feature set count: `),
    },
    refetchInterval: 60 * 1000, // 1 minute
    ...options,
    ...baseOptions,
  });
};

export type RecentlyOpenedFeatureSetsOptions<TData> = UseQueryOptions<RecentlyUsedFeatureSetsResponse, unknown, TData>;

export const useQueryRecentlyOpenedFeatureSets = <TData = RecentlyUsedFeatureSetsResponse>(
  options?: Omit<RecentlyOpenedFeatureSetsOptions<TData>, `queryFn` | `queryKey`>
) => {
  const baseOptions: RecentlyOpenedFeatureSetsOptions<TData> = {
    queryFn: () => fsApi(CoreService.GetRecentlyUsedFeatureSets),
    queryKey: featurestoreQueryKeys.recentlyOpenedFeatureSets,
  };
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

export type RecentlyUsedProjectsOptions<TData> = UseQueryOptions<RecentlyUsedProjectsResponse, unknown, TData>;

export const useQueryRecentlyUsedProjects = <TData = RecentlyUsedProjectsResponse>(
  options?: Omit<RecentlyUsedProjectsOptions<TData>, `queryFn` | `queryKey`>
) => {
  const baseOptions: RecentlyUsedProjectsOptions<TData> = {
    queryFn: () => fsApi(CoreService.GetRecentlyUsedProjects),
    queryKey: featurestoreQueryKeys.recentlyUsedProjects,
  };
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

export function useRefetchPendingReviews() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: featurestoreQueryKeys.featureSetsToReview(), exact: false });
    queryClient.invalidateQueries({ queryKey: featurestoreQueryKeys.featureSetsReview(), exact: false });
  };
}
